/**
 * Friend groups.
 *
 * The one place a captain can widen what other people see of them, so the copy
 * is explicit about the trade: inside a group, the opt-in switch on individual
 * applications stops applying, and members see each other's live anchor-down
 * positions.
 *
 * **The group code is the only way in.** Read it out over VHF, or hand it over
 * on the dock; the other captain types it in. It is rotatable, because a code
 * read out on an open channel is public from that moment.
 *
 * There used to be a second route -- type the other vessel's name and invite
 * her -- and ABG removed it (2026-09-03). It asked a captain to name a yacht
 * they had no other reason to know was a client of ours, and then told them
 * whether she was: "Invited" if the name resolved, an error if it did not. That
 * is a membership oracle on the one fleet this product exists to be discreet
 * about, reachable by anyone with an account and a list of yacht names. A code
 * you were given by somebody who already knows who you are leaks nothing.
 *
 * `POST /api/friends/:id/invite` still exists -- an agent may invite a vessel
 * while acting for a captain, which is a different act with both identities on
 * the row -- but nothing in this app calls it.
 */

import { useCallback, useEffect, useState } from 'react'
import { Empty, Field, Notice, Spinner } from '@abg/ui'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import type { FriendGroup } from '../lib/types'

export function FriendsPage() {
  const { user } = useAuth()
  const [groups, setGroups] = useState<FriendGroup[]>([])
  const [invitations, setInvitations] = useState<FriendGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [joinCode, setJoinCode] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const [mine, pending] = await Promise.all([
        api.get<FriendGroup[]>('/api/friends'),
        api.get<FriendGroup[]>('/api/friends/invitations'),
      ])
      setGroups(mine)
      setInvitations(pending)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load your groups.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await load()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <div className="page"><Spinner label="Loading…" /></div>

  return (
    <div className="page">
      <header className="page-header">
        <h1>Friend groups</h1>
        <span className="subtitle">
          Vessels in a group always see each other's approved plans and anchor positions —
          regardless of the privacy switch on individual applications.
        </span>
      </header>

      {error && <Notice tone="alert">{error}</Notice>}

      {invitations.length > 0 && (
        <section className="card stack">
          <strong>Invitations</strong>
          {invitations.map((group) => (
            <div key={group.id} className="row-between">
              <div>
                <div className="title">{group.name}</div>
                <div className="caption">
                  {group.members.filter((m) => m.accepted).length} vessels already in
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm"
                disabled={busy}
                onClick={() => run(() => api.post(`/api/friends/${group.id}/accept`))}
              >
                Accept
              </button>
            </div>
          ))}
          <p className="caption">
            Accepting lets these vessels see your approved applications and anchor positions.
            Until you accept, they see nothing of yours.
          </p>
        </section>
      )}

      {groups.length === 0 && invitations.length === 0 && (
        <Empty>No groups yet. Create one, or join with a code from another captain.</Empty>
      )}

      {groups.map((group) => (
        <section key={group.id} className="card stack">
          <div className="row-between">
            <strong>{group.name}</strong>
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              disabled={busy}
              onClick={() => run(() => api.delete(`/api/friends/${group.id}/leave`))}
            >
              Leave
            </button>
          </div>

          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} className="stack-sm">
            {group.members.map((member) => (
              <li key={member.vessel_id} className="row-between small">
                <span>
                  {member.vessel_name}
                  {member.vessel_id === user?.vessel_id && ' (you)'}
                </span>
                <span className={member.accepted ? 'badge badge-ok' : 'badge badge-neutral'}>
                  {member.accepted ? 'in' : 'invited'}
                </span>
              </li>
            ))}
          </ul>

          {group.invite_code && (
            <div className="row-between">
              <div>
                <div className="caption">Group code — read this out to invite someone</div>
                <div className="mono" style={{ fontSize: 'var(--fs-h3)', letterSpacing: '.1em' }}>
                  {group.invite_code}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                disabled={busy}
                onClick={() => run(() => api.post(`/api/friends/${group.id}/rotate-code`))}
              >
                New code
              </button>
            </div>
          )}
        </section>
      ))}

      <section className="card stack">
        <strong>Create a group</strong>
        <Field label="Name" hint="Whatever your crew calls the trip.">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Summer cruise"
            maxLength={120}
          />
        </Field>
        <button
          type="button"
          className="btn"
          disabled={busy || !newName.trim()}
          onClick={() =>
            run(async () => {
              await api.post('/api/friends', { name: newName.trim() })
              setNewName('')
            })
          }
        >
          Create
        </button>
      </section>

      <section className="card stack">
        <strong>Join with a code</strong>
        <Field label="Group code" hint="Six to eight letters and digits from another captain.">
          <input
            type="text"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            maxLength={16}
            className="mono"
          />
        </Field>
        <button
          type="button"
          className="btn"
          disabled={busy || joinCode.trim().length < 4}
          onClick={() =>
            run(async () => {
              await api.post('/api/friends/join', { invite_code: joinCode.trim() })
              setJoinCode('')
            })
          }
        >
          Join
        </button>
        <p className="caption">
          Entering a code counts as accepting — the other vessels will be able to see your
          approved plans immediately.
        </p>
      </section>
    </div>
  )
}
